import { clamp, isNil, max, once } from 'lodash';
import { Field } from '@shared/data/entity/fields/Field';
import { getComponentLabel } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { getSubtableCellField } from './subtableColumns';
import { SubtableRowRefs } from './useSubtableRows';

export const MIN_COLUMN_WIDTH = 80;
export const MAX_COLUMN_WIDTH = 300;
/** Inline padding on each side of a cell (2Q - 1px), matching SubtableTable.module.css. */
const CELL_PADDING_INLINE = 15;
/**
 * Room the collection-view renderers add around the text: pill padding and border, a tag's
 * chip, a person's avatar. An estimate; the 300px cap and truncation absorb the difference.
 */
const FIELD_CHROME: Partial<Record<Field['fieldType'], number>> = {
  NumberField: 20,
  TagField: 28,
  PersonField: 36,
  RelatedEntityField: 28,
  DateField: 8,
};

export type MeasureText = (text: string, font: 'header' | 'body') => number;

/**
 * Content-sized column widths from the data rather than the DOM, so the virtualizers know every
 * width up front: the widest of the header label and each loaded row's display text, plus
 * padding and renderer chrome, clamped to [MIN_COLUMN_WIDTH, MAX_COLUMN_WIDTH].
 */
export function getSubtableColumnWidths(
  columns: API.AnyComponent[],
  rows: API.WorkflowEntityFragment[],
  refs: SubtableRowRefs | undefined,
  measureText: MeasureText,
): number[] {
  return columns.map(column => {
    const headerWidth = measureText((getComponentLabel(column) ?? '').toUpperCase(), 'header');
    const cellWidths = rows.map(row => {
      const field = getSubtableCellField(row, column, refs);
      if (isNil(field) || field.isEmpty) {
        return 0;
      }
      return measureText(field.toDisplayString(), 'body') + (FIELD_CHROME[field.fieldType] ?? 0);
    });
    const contentWidth = max([headerWidth, ...cellWidths]) ?? 0;
    return clamp(
      Math.ceil(contentWidth + CELL_PADDING_INLINE * 2),
      MIN_COLUMN_WIDTH,
      MAX_COLUMN_WIDTH,
    );
  });
}

/** The canvas and font family, set up once; null where there's no canvas (tests). */
const getMeasuringContext = once(() => {
  if (typeof document === 'undefined') {
    return null;
  }
  const context = document.createElement('canvas').getContext('2d');
  if (isNil(context)) {
    return null;
  }
  const family =
    getComputedStyle(document.documentElement).getPropertyValue('--system-font-family').trim() ||
    'system-ui, sans-serif';
  return { context, family };
});

/** Canvas text measurement in the header (overline) and body1 fonts. */
export function measureTextOnCanvas(text: string, font: 'header' | 'body'): number {
  const measuring = getMeasuringContext();
  if (isNil(measuring)) {
    // A rough average glyph width.
    return text.length * 8;
  }
  const { context, family } = measuring;
  context.font = font === 'header' ? `600 12px ${family}` : `400 16px ${family}`;
  return context.measureText(text).width;
}
